import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/utils.js';

export type ButtonVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'neutral-outline'
  | 'primary-outline'
  | 'success'
  | 'link';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Square icon-only button (maps to `.btn--icon`). */
  iconOnly?: boolean;
  /** Active state for secondary buttons inside a `ButtonGroup`. */
  active?: boolean;
  children?: ReactNode;
}

const variantClassName: Record<ButtonVariant, string | undefined> = {
  default: undefined,
  primary: 'btn--primary',
  secondary: 'btn--secondary',
  ghost: 'btn--ghost',
  danger: 'btn--danger',
  'danger-outline': 'btn--danger-outline',
  'neutral-outline': 'btn--neutral-outline',
  'primary-outline': 'btn--primary-outline',
  success: 'btn--success',
  link: 'btn--link',
};

const sizeClassName: Record<ButtonSize, string | undefined> = {
  xs: 'btn--xs',
  sm: 'btn--sm',
  md: undefined,
  lg: 'btn--lg',
  xl: 'btn--xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    iconOnly = false,
    active = false,
    className,
    type = 'button',
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'btn',
        variantClassName[variant],
        sizeClassName[size],
        iconOnly && 'btn--icon',
        active && 'is-active',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

export interface ButtonIconProps {
  children: ReactNode;
  className?: string;
}

/** Wraps icon content inside a button (maps to `.btn-icon`). */
export function ButtonIcon({ children, className }: ButtonIconProps) {
  return <span className={cn('btn-icon', className)}>{children}</span>;
}
